// lib/simulatorMath.js — حساب الربح والهامش لمحاكي العروض/الكومبو.
// نقطة الحقيقة الوحيدة للحساب (يستوردها Simulator.jsx والاختبارات).
//
// العلة المصحَّحة: أسعار POS شاملة ضريبة 15%، والتكلفة في النظام بلا
// ضريبة. يجب استبعاد الضريبة من سعر البيع قبل طرح التكلفة:
//   السعر بلا ضريبة = الشامل ÷ (1 + نسبة الضريبة).
// نسبة الضريبة تأتي من tenant_settings (vat_rate) لا hardcoded.

export const DEFAULT_VAT_RATE = 0.15;

function exVat(priceIncVat, vatRate) {
  const r = Number(vatRate);
  const denom = 1 + (Number.isFinite(r) ? r : DEFAULT_VAT_RATE);
  return denom > 0 ? Number(priceIncVat) / denom : Number(priceIncVat);
}

// محاكي العروض: خصم على سعر القائمة (شامل ضريبة) ثم استبعاد الضريبة.
// unitCost = null/undefined ⇒ لا توجد تكلفة مرفوعة (يختلف عن 0 المصرَّحة):
// لا نحسب ربحاً ولا هامشاً في هذه الحالة.
export function offerMath({ priceIncVat, unitCost, discountPct, vatRate = DEFAULT_VAT_RATE }) {
  const pInc = Number(priceIncVat) || 0;
  const d = Number(discountPct) || 0;
  const hasCost = unitCost !== null && unitCost !== undefined && Number.isFinite(Number(unitCost));
  const cost = hasCost ? Number(unitCost) : null;

  const priceIncVatVal = pInc;
  const priceExVat = exVat(pInc, vatRate);                 // السعر الأصلي بلا ضريبة
  const priceAfterIncVat = pInc * (1 - d / 100);           // بعد الخصم (شامل)
  const priceAfterExVat = exVat(priceAfterIncVat, vatRate); // بعد الخصم (بلا ضريبة)
  const profit = hasCost ? priceAfterExVat - cost : null;   // الربح على أساس بلا ضريبة
  const marginPct = hasCost && priceAfterExVat > 0 ? (profit / priceAfterExVat) * 100 : null;
  const discountAmountIncVat = priceIncVatVal - priceAfterIncVat;

  return {
    vatRate: Number(vatRate),
    hasCost,
    priceIncVat: priceIncVatVal,
    priceExVat,
    priceAfterIncVat,
    priceAfterExVat,
    unitCost: cost,
    profit,
    marginPct,
    discountAmountIncVat,
    healthy: hasCost ? profit > 0 : null,
  };
}

// محاكي الكومبو: منطق منفصل — السعر المقترح مشتق من التكلفة وهامش مستهدف
// (أساس بلا ضريبة). أي منتج بلا تكلفة مرفوعة ⇒ لا حساب (hasCost=false).
export function comboMath({ items = [], targetMarginPct = 50, vatRate = DEFAULT_VAT_RATE }) {
  const sel = (items || []).filter(Boolean);
  const missingCost = sel.some(
    (x) => x.unitCost === null || x.unitCost === undefined || !Number.isFinite(Number(x.unitCost))
  );
  const hasCost = sel.length > 0 && !missingCost;
  const sumPriceIncVat = sel.reduce((s, x) => s + (Number(x.unitPrice) || 0), 0);
  const sumCost = hasCost ? sel.reduce((s, x) => s + (Number(x.unitCost) || 0), 0) : null;
  const m = Number(targetMarginPct) || 0;

  const suggestedExVat = hasCost && m < 100 ? sumCost / (1 - m / 100) : null;
  const suggestedIncVat = suggestedExVat != null ? suggestedExVat * (1 + (Number(vatRate) || DEFAULT_VAT_RATE)) : null;
  const profit = suggestedExVat != null ? suggestedExVat - sumCost : null;
  const realMarginPct = suggestedExVat != null && suggestedExVat > 0 ? (profit / suggestedExVat) * 100 : null;
  const savingsVsSeparateIncVat = suggestedIncVat != null ? Math.max(0, sumPriceIncVat - suggestedIncVat) : null;

  return {
    vatRate: Number(vatRate),
    hasCost,
    sumPriceIncVat,
    sumCost,
    suggestedExVat,
    suggestedIncVat,
    profit,
    realMarginPct,
    savingsVsSeparateIncVat,
  };
}
